/**
 * Auto-reloads the app silently when a new build is detected on the server,
 * OR when the browser fails to load a JS chunk (which happens when the user
 * was running v1 and we just deployed v2 — the v1 `index.html` is still in
 * their tab pointing at `main.abc123.js` which no longer exists).
 *
 * Two triggers:
 *   1. Periodic `/api/version` poll (every 60s + every time the tab becomes
 *      visible). If the live build_id differs from what we saw on first load,
 *      hard-reload silently.
 *   2. Global `error` listener catches `ChunkLoadError` and hard-reloads
 *      immediately. This is the "you're already on a stale page and just
 *      clicked something" case.
 */
import { api } from "@/lib/api";

const STORAGE_KEY = "ff_seen_build_id";
const POLL_MS = 60_000;
let initialBuildId = null;
let timer = null;

function hardReload() {
  // Tell any SplashScreen mounted at the top of the tree to swap to
  // "Updating…" mode, then give it a brief moment to paint before we
  // actually reload so the flash feels intentional not glitchy.
  try {
    window.dispatchEvent(new Event("ff:updating"));
  } catch { /* older browsers */ }
  // Best-effort cache wipe before reload — covers WebView (mobile app).
  try {
    if ("caches" in window) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  // Bust any HTTP cache the browser is holding onto.
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  setTimeout(() => window.location.replace(url.toString()), 900);
}

async function checkVersion() {
  try {
    const { data } = await api.get("/version");
    const current = data?.build_id;
    if (!current) return;
    if (!initialBuildId) {
      initialBuildId = current;
      try { localStorage.setItem(STORAGE_KEY, current); } catch { /* ignore */ }
      return;
    }
    if (current !== initialBuildId) {
      // A new build is live — hard-reload to pick up the fresh `index.html`.
      hardReload();
    }
  } catch {
    // Don't spam errors — silent fail is fine for a version probe.
  }
}

export function startVersionCheck() {
  // Run on mount, then every minute, then every time the tab becomes visible.
  checkVersion();
  timer = setInterval(checkVersion, POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkVersion();
  });

  // Catch chunk-load errors and reload immediately.
  window.addEventListener("error", (event) => {
    const msg = String(event?.message || "");
    const isChunkError =
      /Loading chunk \d+ failed/.test(msg) ||
      /ChunkLoadError/.test(msg) ||
      /Loading CSS chunk/.test(msg) ||
      (event?.error?.name === "ChunkLoadError");
    if (isChunkError) {
      hardReload();
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (reason && (reason.name === "ChunkLoadError" || /Loading chunk/.test(String(reason?.message || "")))) {
      hardReload();
    }
  });
}

export function stopVersionCheck() {
  if (timer) clearInterval(timer);
}
